// Implements: F-IDN-014
/**
 * The two-step verification: enrol (needs the current password), verify the first code, disable.
 * Verifying and disabling change `totp_enabled`, so both refresh the own profile. The enrolment's
 * secret is NOT kept in the cache (`gcTime: 0` and the mutation is reset by its container): it is
 * shown once and lives only in the component that shows it.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type { TotpEnrolment } from "../../../types/api";
import { ACCOUNT_ME_KEY, disableTotp, enrolTotp, verifyTotp } from "../api/accountApi";

/** A `204` call as a mutation that answers `null` (a mutation of `void` is not a type the linter accepts). */
function nulled(call: (code: string) => Promise<void>): (code: string) => Promise<null> {
  return async (code) => {
    await call(code);
    return null;
  };
}

export interface UseTotpResult {
  readonly enrol: UseMutationResult<TotpEnrolment, ApiError, string>;
  readonly verify: UseMutationResult<null, ApiError, string>;
  readonly disable: UseMutationResult<null, ApiError, string>;
}

export function useTotp(): UseTotpResult {
  const client = useQueryClient();
  const refreshMe = async (): Promise<void> => {
    await client.invalidateQueries({ queryKey: ACCOUNT_ME_KEY });
  };
  const enrol = useMutation<TotpEnrolment, ApiError, string>({ mutationFn: enrolTotp, gcTime: 0 });
  const verify = useMutation<null, ApiError, string>({ mutationFn: nulled(verifyTotp), onSuccess: refreshMe });
  const disable = useMutation<null, ApiError, string>({ mutationFn: nulled(disableTotp), onSuccess: refreshMe });
  return { enrol, verify, disable };
}
